import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, trackEvent } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { SITE_URL } from "@/lib/seoMeta";
import { IMAGES } from "@/lib/site";
import { useLang } from "@/context/LanguageContext";
import { loc, locArr } from "@/lib/localize";
import { Breadcrumbs } from "@/components/common/Breadcrumbs";
import { FAQAccordion } from "@/components/common/FAQAccordion";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, AlertTriangle, ExternalLink } from "lucide-react";

const T = {
  en: {
    notFound: "Broker not found", back: "Back to partners", placeholder: "Placeholder partner",
    openWith: (n) => `Open Account With ${n}`, overview: "Overview", regulation: "Regulation",
    trading: "Trading", funding: "Funding", faqs: "FAQs", company: "Company Information",
    advantages: "Advantages", considerations: "Important Considerations", regInfo: "Regulatory Information",
    markets: "Available Markets", platforms: "Trading Platforms", accounts: "Account Types",
    spreads: "Spreads / Commissions", funInfo: "Deposit & Withdrawal Information", minDep: "Minimum deposit",
    keyFacts: "Key Facts", regulationLbl: "Regulation", minDepLbl: "Minimum Deposit", keyFeatures: "Key Features",
    openAccount: "Open Account",
    riskTitle: "Risk Information: ",
    risk: "Trading leveraged instruments involves a high level of risk. Brokerage services, execution and custody are provided by the broker, not by MENAGroupFX.",
    loading: "Loading...",
  },
  ar: {
    notFound: "الوسيط غير موجود", back: "العودة إلى الشركاء", placeholder: "شريك مبدئي",
    openWith: (n) => `فتح حساب مع ${n}`, overview: "نظرة عامة", regulation: "التنظيم",
    trading: "التداول", funding: "التمويل", faqs: "الأسئلة الشائعة", company: "معلومات الشركة",
    advantages: "المزايا", considerations: "اعتبارات مهمة", regInfo: "المعلومات التنظيمية",
    markets: "الأسواق المتاحة", platforms: "منصات التداول", accounts: "أنواع الحسابات",
    spreads: "الفروقات / العمولات", funInfo: "معلومات الإيداع والسحب", minDep: "أدنى إيداع",
    keyFacts: "حقائق أساسية", regulationLbl: "التنظيم", minDepLbl: "أدنى إيداع", keyFeatures: "أهم المزايا",
    openAccount: "فتح حساب",
    riskTitle: "معلومات المخاطر: ",
    risk: "ينطوي تداول الأدوات ذات الرافعة على مستوى عالٍ من المخاطر. تُقدَّم خدمات الوساطة والتنفيذ والحفظ من قبل الوسيط، وليس من MENAGroupFX.",
    loading: "جارٍ التحميل...",
  },
};

export default function BrokerProfile() {
  const { slug } = useParams();
  const { isAr } = useLang();
  const t = isAr ? T.ar : T.en;
  const { data: b, isLoading, isError } = useQuery({
    queryKey: ["broker", slug],
    queryFn: async () => (await api.get(`/brokers/${slug}`)).data,
    retry: false,
  });
  useSeo({ title: b?.seo_title || b?.name, description: b?.seo_description || loc(b, "tagline", isAr), image: b?.logo_url || IMAGES.skyline, canonical: `${SITE_URL}/broker-partners/${slug}`, locale: isAr ? "ar_AE" : "en_US" });

  if (isLoading) return <div className="container-tight py-32 text-center text-subtle">{t.loading}</div>;
  if (isError || !b) return (
    <div className="container-tight py-32 text-center">
      <h1 className="font-heading text-2xl font-semibold text-ink">{t.notFound}</h1>
      <Button asChild className="mt-6 bg-finblue hover:bg-finblueDark"><Link to="/broker-partners">{t.back}</Link></Button>
    </div>
  );

  const name = loc(b, "name", isAr);
  const initials = b.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const openAccount = () => {
    trackEvent("broker_referral_click", { broker: b.slug });
    api.post(`/brokers/${b.slug}/click`, { utm: {} }).catch(() => {});
    window.open(b.tracking_url || b.external_url || "#", "_blank");
  };

  const Info = ({ label, value }) => (
    <div className="border-b border-hair py-3">
      <p className="text-xs uppercase tracking-wide text-subtle">{label}</p>
      <p className="mt-1 font-medium text-ink">{value || "—"}</p>
    </div>
  );

  return (
    <>
      <section className="relative overflow-hidden bg-navy">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="container-tight relative py-14">
          <div className="[&_a]:text-slate-400 [&_a:hover]:text-gold [&_span]:text-white">
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Broker Partners", to: "/broker-partners" }, { label: name }]} />
          </div>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              {b.logo_url ? (
                <img src={b.logo_url} alt={name} className="h-16 w-16 rounded-xl border border-white/10 bg-white object-contain p-1.5" />
              ) : (
                <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-white/10 font-heading text-xl font-bold text-gold">{initials}</span>
              )}
              <div>
                <span className="inline-block rounded bg-white/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gold">{t.placeholder}</span>
                <h1 className="mt-1 font-heading text-3xl font-bold text-white">{name}</h1>
                <p className="mt-1 max-w-xl text-sm text-slate-300">{loc(b, "tagline", isAr)}</p>
              </div>
            </div>
            <Button size="lg" onClick={openAccount} className="bg-finblue hover:bg-finblueDark" data-testid="broker-open-account">
              {t.openWith(name)} <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-tight grid gap-10 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <Tabs defaultValue="overview">
              <TabsList className="mb-6 flex-wrap">
                <TabsTrigger value="overview" data-testid="tab-overview">{t.overview}</TabsTrigger>
                <TabsTrigger value="regulation">{t.regulation}</TabsTrigger>
                <TabsTrigger value="trading">{t.trading}</TabsTrigger>
                <TabsTrigger value="funding">{t.funding}</TabsTrigger>
                <TabsTrigger value="faqs">{t.faqs}</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <p className="leading-relaxed text-subtle">{loc(b, "overview", isAr)}</p>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{t.company}</h3>
                  <p className="mt-2 text-sm text-subtle">{loc(b, "company_information", isAr)}</p>
                </div>
                <div className="grid gap-6 sm:grid-cols-2">
                  <List title={t.advantages} items={locArr(b, "advantages", isAr)} icon={Check} tone="text-success" />
                  <List title={t.considerations} items={locArr(b, "considerations", isAr)} icon={AlertTriangle} tone="text-danger" />
                </div>
              </TabsContent>

              <TabsContent value="regulation" className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink">{t.regInfo}</h3>
                <p className="text-sm text-subtle">{loc(b, "regulatory_information", isAr)}</p>
                <div className="flex flex-wrap gap-2">
                  {locArr(b, "regulatory_jurisdictions", isAr).map((j) => <span key={j} className="rounded-md border border-hair bg-canvas px-3 py-1 text-sm text-ink">{j}</span>)}
                </div>
              </TabsContent>

              <TabsContent value="trading" className="space-y-6">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{t.markets}</h3>
                  <div className="mt-3 flex flex-wrap gap-2">{locArr(b, "markets", isAr).map((m) => <span key={m} className="rounded-md border border-hair bg-canvas px-3 py-1 text-sm text-ink">{m}</span>)}</div>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{t.platforms}</h3>
                  <div className="mt-3 flex flex-wrap gap-2">{locArr(b, "platforms", isAr).map((m) => <span key={m} className="rounded-md border border-hair bg-canvas px-3 py-1 text-sm text-ink">{m}</span>)}</div>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{t.accounts}</h3>
                  <div className="mt-3 flex flex-wrap gap-2">{locArr(b, "account_types", isAr).map((m) => <span key={m} className="rounded-md border border-hair bg-canvas px-3 py-1 text-sm text-ink">{m}</span>)}</div>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{t.spreads}</h3>
                  <p className="mt-2 text-sm text-subtle">{loc(b, "spreads_info", isAr)}</p>
                </div>
              </TabsContent>

              <TabsContent value="funding" className="space-y-4">
                <h3 className="font-heading text-lg font-semibold text-ink">{t.funInfo}</h3>
                <p className="text-sm text-subtle">{loc(b, "deposit_withdrawal_info", isAr)}</p>
                <div className="rounded-lg border border-hair bg-canvas p-4 text-sm text-subtle">{t.minDep}: <span className="font-semibold text-ink">{b.min_deposit}</span></div>
              </TabsContent>

              <TabsContent value="faqs">
                <FAQAccordion items={b.faqs || []} idPrefix="broker-faq" />
              </TabsContent>
            </Tabs>

            <div className="mt-10 flex gap-3 rounded-lg border border-hair bg-canvas p-4">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              <p className="text-xs leading-relaxed text-subtle">
                <span className="font-semibold text-ink">{t.riskTitle}</span>
                {t.risk}
              </p>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-xl border border-hair bg-white p-6 shadow-sm">
              <h3 className="font-heading text-lg font-semibold text-ink">{t.keyFacts}</h3>
              <Info label={t.regulationLbl} value={locArr(b, "regulatory_jurisdictions", isAr).join(", ")} />
              <Info label={t.minDepLbl} value={b.min_deposit} />
              <Info label={t.platforms} value={locArr(b, "platforms", isAr).join(", ")} />
              <Info label={t.markets} value={locArr(b, "markets", isAr).join(", ")} />
              <Info label={t.accounts} value={locArr(b, "account_types", isAr).join(", ")} />
              <div className="mt-4 space-y-2">
                <p className="text-xs uppercase tracking-wide text-subtle">{t.keyFeatures}</p>
                <ul className="space-y-1.5">
                  {locArr(b, "features", isAr).map((ftr) => (
                    <li key={ftr} className="flex items-start gap-2 text-sm text-ink"><Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />{ftr}</li>
                  ))}
                </ul>
              </div>
              <Button onClick={openAccount} className="mt-6 w-full bg-finblue hover:bg-finblueDark" data-testid="broker-open-account-side">{t.openAccount} <ExternalLink className="h-4 w-4" /></Button>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function List({ title, items, icon: Icon, tone }) {
  return (
    <div className="rounded-xl border border-hair bg-white p-5 shadow-sm">
      <h4 className="font-heading text-base font-semibold text-ink">{title}</h4>
      <ul className="mt-3 space-y-2">
        {(items || []).map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm text-subtle"><Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />{it}</li>
        ))}
      </ul>
    </div>
  );
}
